import { useState, type ReactNode } from 'react';

import { AcButton, AcSelect, ModalShell } from '@/dashboard/ui';
import { cancelReasonLabel } from '@/dashboard/appointments/cancel-reasons';

/**
 * FRONTDESK-CHECKIN-AND-IDENTITY-V1 (rider R-08) -- the day board's
 * cancel-reason picker. ONE-VOCABULARY LAW (LES-009's real divergence
 * risk): the token list + labels live in the ONE cancel-reasons module
 * (cancel-reasons.ts), which THIS dialog and the appointment workbench's
 * inline confirm-strip select BOTH consume -- two surface-idiomatic
 * mountings (overlay here; the workbench's not-converted strip idiom
 * there), one vocabulary, one label source, zero drift.
 *
 * The picker REQUIRES a selection before the destructive confirm unlocks
 * (the vocabulary includes 'other', so a reason always exists to pick); the
 * historical SCHEMA stays nullable; Reception also requires a reason at
 * the protected API. NO free-text note
 * (the appointment_events doctrine; 'other' stands alone).
 *
 * Vocabulary: the caller passes the org's resolved codes
 * (resolveCancelReasonCodes(me)); labels ride the label module.
 */

export const CANCEL_DIALOG_TITLE = 'Cancel appointment?';
export const CANCEL_DIALOG_EYEBROW = '';
export const CANCEL_DIALOG_PICK_LABEL = 'Reason';
export const CANCEL_DIALOG_CONFIRM_COPY = 'Cancel appointment';
export const CANCEL_DIALOG_KEEP_COPY = 'Keep';

export interface CancelReasonDialogProps {
  readonly patientName?: string;
  readonly appointmentLabel?: string;
  readonly details?: ReactNode;
  /** The org's resolved reason vocabulary (never empty). */
  readonly codes: readonly string[];
  /** True while the cancel mutation is in flight (controls disable). */
  readonly busy: boolean;
  readonly error?: string | null;
  /** Fires with the PICKED token; the caller runs the mutation. */
  readonly onConfirm: (reasonCode: string) => void;
  readonly onClose: () => void;
}

export default function CancelReasonDialog({
  patientName,
  appointmentLabel,
  details,
  codes,
  busy,
  error,
  onConfirm,
  onClose,
}: CancelReasonDialogProps): JSX.Element {
  const [picked, setPicked] = useState<string>('');
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <ModalShell
      trapFocus
      hideCloseButton
      title={CANCEL_DIALOG_TITLE}
      onClose={onClose}
      closeDisabled={busy}
      closeOnScrim={!busy}
      closeOnEscape={!busy}
      testId="cancel-reason-dialog"
      footer={
        <div className="cancel-reason__actions">
          <AcButton
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={onClose}
            data-testid="cancel-reason-keep"
          >
            {CANCEL_DIALOG_KEEP_COPY}
          </AcButton>
          <AcButton
            size="sm"
            variant="primary"
            disabled={busy || !codes.includes(picked)}
            onClick={() => {
              if (codes.includes(picked)) onConfirm(picked);
            }}
            data-testid="cancel-reason-confirm"
          >
            {busy ? 'Cancelling...' : CANCEL_DIALOG_CONFIRM_COPY}
          </AcButton>

        </div>
      }
    >
      <p className="cancel-reason__summary">
        {patientName ? <strong data-testid="cancel-reason-patient">{patientName}</strong> : null}
        {patientName && appointmentLabel ? ' · ' : null}
        {appointmentLabel ? <span data-testid="cancel-reason-appointment">{appointmentLabel}</span> : null}
      </p>
      {details ? <details className="cancel-reason__details" onToggle={event => setDetailsOpen(event.currentTarget.open)}>
        <summary>Details</summary>
        {detailsOpen ? <div className="cancel-reason__content">{details}</div> : null}
      </details> : null}
      <label className="cancel-reason__field">
        {CANCEL_DIALOG_PICK_LABEL}
        <AcSelect
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          disabled={busy}
          data-testid="cancel-reason-select"
        >
          <option value="">Choose a reason</option>
          {codes.map((code) => (
            <option key={code} value={code}>
              {cancelReasonLabel(code)}
            </option>
          ))}
        </AcSelect>
      </label>
      {error ? <p role="alert">{error}</p> : null}
    </ModalShell>
  );
}
